import * as storage from "./storage"
import { clearOpenAnalystAccessToken, getOpenAnalystAccessToken, getOpenAnalystAccountAccessToken, setAccessToken } from "./token"
import { clearStoredTokens } from "./desktop-token-store"
import type { AuthIntentPreview, AuthRole, AuthSession, PendingDesktopOnboarding, SignUpInput } from "./types"
import {
  openAnalystAccountBaseUrl,
  openAnalystTenxBaseUrl,
  openAnalystWebBaseUrl,
} from "@/utils/openanalyst-hosts"

let isDesktop = false
let desktopAccountFetch: typeof fetch | null = null
const desktopAuthContext: {
  userId?: string | null
  userEmail?: string | null
  fullName?: string | null
  firstName?: string | null
  lastName?: string | null
  orgId?: string | null
  workspaceId?: string | null
  projectId?: string | null
  role?: AuthRole | null
  accountType?: AuthSession["accountType"] | null
  exp?: number | null
  bindingTenantId?: string | null
  bindingVersion?: number | null
} = {}

export function configureDesktopMode(enabled: boolean): void {
  isDesktop = enabled
}

export function configureDesktopAccountFetch(fetchImpl: typeof fetch | null): void {
  desktopAccountFetch = fetchImpl
}

type ErrorPayload = {
  error?: {
    message?: string
    code?: string
    metadata?: unknown
    session?: string
    challenge?: string
  }
  message?: string
  session?: string
  challenge?: string
}

export type AuthProfile = {
  _id?: string
  userId?: string
  email?: string
  fullName?: string
  firstName?: string
  lastName?: string
  orgId?: string
  workspaceId?: string
  projectId?: string
  role?: AuthRole
  accountType?: "individual" | "business"
}

function authBaseUrl() {
  return openAnalystWebBaseUrl()
}

function tenxAuthBaseUrl() {
  return openAnalystTenxBaseUrl()
}

function authUrl(path: string) {
  return `${authBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`
}

function isSmartworkHostedAuthBaseUrl() {
  return /^https:\/\/(?:cloud\.openanalyst\.com|smartwork\.(?:arjun\.tv|openanalyst\.com))$/.test(authBaseUrl())
}

function smartworkHostedTenxBridgePath(path: string) {
  if (!isSmartworkHostedAuthBaseUrl()) return null
  if (path === "/v2/auth/passwordless/start") return "/api/v1/userAccount/tenx/passwordless/start"
  if (path === "/v2/auth/passwordless/confirm") return "/api/v1/userAccount/tenx/passwordless/confirm"
  if (path === "/v2/auth/openanalyst/auth-intents/confirm") return "/api/v1/userAccount/tenx/auth-intents/confirm"
  return null
}

function tenxAuthUrl(path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  const bridgePath = smartworkHostedTenxBridgePath(normalizedPath)
  return bridgePath ? `${authBaseUrl()}${bridgePath}` : `${tenxAuthBaseUrl()}${normalizedPath}`
}

function openAnalystAccountUrl(path: string) {
  return `${openAnalystAccountBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`
}

async function parseJson(response: Response) {
  const text = await response.text()
  if (!text) return undefined
  try {
    return JSON.parse(text) as unknown
  } catch {
    const cleaned = text.trim().replace(/^,+|,+$/g, "")
    try {
      const parsed = JSON.parse(`[${cleaned}]`) as unknown
      const objects = Array.isArray(parsed)
        ? parsed.filter((item): item is Record<string, unknown> => !!item && typeof item === "object" && !Array.isArray(item))
        : []
      if (objects.length) return Object.assign({}, ...objects)
    } catch {}
    return { message: text } as ErrorPayload
  }
}

export class AuthApiError extends Error {
  status: number
  code?: string
  metadata?: unknown

  constructor(input: { status: number; message: string; code?: string; metadata?: unknown }) {
    super(input.message)
    this.name = "AuthApiError"
    this.status = input.status
    this.code = input.code
    this.metadata = input.metadata
  }
}

export const OPENANALYST_ONBOARDING_REQUIRED = "OPENANALYST_ONBOARDING_REQUIRED"

export class OpenAnalystOnboardingRequiredError extends AuthApiError {
  data: PendingDesktopOnboarding

  constructor(data: PendingDesktopOnboarding) {
    super({
      status: 409,
      message: "OpenAnalyst desktop onboarding is required.",
      code: OPENANALYST_ONBOARDING_REQUIRED,
    })
    this.name = "OpenAnalystOnboardingRequiredError"
    this.data = data
  }
}

async function toError(response: Response) {
  const payload = (await parseJson(response)) as ErrorPayload | undefined
  const retryMetadata =
    payload?.session || payload?.challenge || payload?.error?.session || payload?.error?.challenge
      ? {
          ...(payload?.error?.metadata && typeof payload.error.metadata === "object" && !Array.isArray(payload.error.metadata)
            ? payload.error.metadata
            : {}),
          ...(payload?.session || payload?.error?.session ? { session: payload.session ?? payload.error?.session } : {}),
          ...(payload?.challenge || payload?.error?.challenge ? { challenge: payload.challenge ?? payload.error?.challenge } : {}),
        }
      : payload?.error?.metadata
  const message = payload?.error?.message || payload?.message || `Request failed with status ${response.status}`
  return new AuthApiError({
    status: response.status,
    message,
    code: payload?.error?.code,
    metadata: retryMetadata,
  })
}

async function request<T>(
  path: string,
  input?: {
    method?: string
    body?: unknown
    headers?: HeadersInit
    withSessionToken?: boolean
    authToken?: "openanalyst" | "account"
  },
) {
  const headers = new Headers(input?.headers)
  headers.set("Content-Type", "application/json")
  if (input?.withSessionToken !== false) {
    const token = storage.getSessionToken()
    if (token) headers.set("x-session-token", token)
  }
  const accessToken =
    input?.authToken === "account" || (!input?.authToken && isSmartworkHostedAuthBaseUrl())
      ? getOpenAnalystAccountAccessToken()
      : getOpenAnalystAccessToken() ?? getOpenAnalystAccountAccessToken()
  if (accessToken && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${accessToken}`)
  const response = await fetch(authUrl(path), {
    method: input?.method ?? "GET",
    credentials: "include",
    headers,
    body: input?.body === undefined ? undefined : JSON.stringify(input.body),
  })
  if (!response.ok) throw await toError(response)
  const data = (await parseJson(response)) as T
  return { data, response }
}

export async function openAnalystAccountRequest<T>(path: string, input?: { method?: string; body?: unknown; headers?: HeadersInit }) {
  const headers = new Headers(input?.headers)
  headers.set("Content-Type", "application/json")
  const accessToken = getOpenAnalystAccountAccessToken()
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`)
  const fetcher = isDesktop && desktopAccountFetch ? desktopAccountFetch : fetch
  const response = await fetcher(openAnalystAccountUrl(path), {
    method: input?.method ?? "GET",
    credentials: "include",
    headers,
    body: input?.body === undefined ? undefined : JSON.stringify(input.body),
  })
  if (!response.ok) throw await toError(response)
  const data = (await parseJson(response)) as T
  return { data, response }
}

async function tenxRequest<T>(path: string, input?: { method?: string; body?: unknown }) {
  const headers = new Headers()
  if (input?.body !== undefined) headers.set("Content-Type", "application/json")
  const response = await fetch(tenxAuthUrl(path), {
    method: input?.method ?? "GET",
    credentials: "include",
    headers,
    body: input?.body === undefined ? undefined : JSON.stringify(input.body),
  })
  if (!response.ok) throw await toError(response)
  return (await parseJson(response)) as T
}

type TokenPayload = {
  accessToken?: string | null
  access_token?: string | null
  refreshToken?: string | null
  refresh_token?: string | null
  idToken?: string | null
  id_token?: string | null
  expiresIn?: number | null
  expires_in?: number | null
  expiresAt?: number | null
  expires_at?: number | null
  tokenType?: string | null
  token_type?: string | null
  userEmail?: string | null
  user_email?: string | null
}

type AuthTokenPayload = TokenPayload & {
  openAnalyst?: TokenPayload | null
  controlPlane?: TokenPayload | null
}

function tokenPayload(value: TokenPayload | null | undefined) {
  return value && typeof value === "object" ? value : null
}

function hasOpenAnalystSlot(value: AuthTokenPayload | null | undefined) {
  return !!value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "openAnalyst")
}

function payloadToken(payload: TokenPayload | null | undefined, camel: keyof TokenPayload, snake: keyof TokenPayload) {
  if (!payload) return null
  const value = typeof payload[camel] === "string" ? payload[camel] : payload[snake]
  return typeof value === "string" && value.trim() ? value.trim() : null
}

function tokenClaims(token: string | null) {
  const part = token?.split(".")[1]
  if (!part) return null
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/")
    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))) as Record<string, unknown>
  } catch {
    return null
  }
}

export async function applyAuthTokens(payload: AuthTokenPayload) {
  const controlPlane = tokenPayload(payload.controlPlane)
  const topLevelToken = payloadToken(payload, "accessToken", "access_token")
  if (topLevelToken && (!controlPlane || hasOpenAnalystSlot(payload))) {
    setAccessToken(topLevelToken, "openanalyst_account")
  }
  const openAnalystToken =
    payloadToken(tokenPayload(payload.openAnalyst), "accessToken", "access_token") ??
    (controlPlane || !hasOpenAnalystSlot(payload) ? topLevelToken : null)
  if (openAnalystToken) setAccessToken(openAnalystToken, "openanalyst")
  else if (hasOpenAnalystSlot(payload)) clearOpenAnalystAccessToken()
  const controlPlanePayload = controlPlane ?? payload
  const accessToken = payloadToken(controlPlanePayload, "accessToken", "access_token")
  if (!accessToken) return false
  setAccessToken(accessToken, "tenx_control_plane")
  const idToken = payloadToken(controlPlanePayload, "idToken", "id_token")
  const accessClaims = tokenClaims(accessToken)
  const idClaims = tokenClaims(idToken)
  const openAnalystClaims = tokenClaims(openAnalystToken)
  desktopAuthContext.userId = cleanString(openAnalystClaims?.userId) || desktopAuthContext.userId
  desktopAuthContext.userEmail =
    cleanString(openAnalystClaims?.email) ||
    cleanString(controlPlanePayload.userEmail) ||
    cleanString(controlPlanePayload.user_email) ||
    cleanString(idClaims?.email)
  desktopAuthContext.fullName = cleanString(openAnalystClaims?.fullName) || desktopAuthContext.fullName
  desktopAuthContext.firstName = cleanString(openAnalystClaims?.firstName) || desktopAuthContext.firstName
  desktopAuthContext.lastName = cleanString(openAnalystClaims?.lastName) || desktopAuthContext.lastName
  desktopAuthContext.orgId = cleanString(openAnalystClaims?.orgId) || desktopAuthContext.orgId
  desktopAuthContext.workspaceId = cleanString(openAnalystClaims?.workspaceId) || desktopAuthContext.workspaceId
  desktopAuthContext.projectId = cleanString(openAnalystClaims?.projectId) || desktopAuthContext.projectId
  desktopAuthContext.role = cleanRole(openAnalystClaims?.role) || desktopAuthContext.role
  desktopAuthContext.accountType = cleanAccountType(openAnalystClaims?.accountType) || desktopAuthContext.accountType
  desktopAuthContext.exp = cleanExp(accessClaims?.exp) || cleanExp(idClaims?.exp) || desktopAuthContext.exp
  desktopAuthContext.bindingTenantId =
    cleanString(accessClaims?.oa_binding_tenant_id) ||
    cleanString(idClaims?.oa_binding_tenant_id) ||
    desktopAuthContext.bindingTenantId
  desktopAuthContext.bindingVersion =
    cleanBindingVersion(accessClaims?.oa_binding_version) ||
    cleanBindingVersion(idClaims?.oa_binding_version) ||
    desktopAuthContext.bindingVersion
  return true
}

function openAnalystPasswordlessMarker() {
  return {
    application: "openanalyst",
    openanalyst: true,
    client: "openanalyst-web",
  }
}

export type PasswordlessCodeDelivery = {
  destination?: string | null
  deliveryMedium?: string | null
  attributeName?: string | null
}

export type PasswordlessStartResult = AuthTokenPayload & {
  ok?: boolean
  challenge?: string | null
  session?: string | null
  codeDeliveryDetails?: PasswordlessCodeDelivery | null
  codeDelivery?: PasswordlessCodeDelivery | null
}

export type PasswordlessIntent = "signin" | "signup"

export async function startPasswordlessSignIn(
  email: string,
  intent: PasswordlessIntent = "signin",
): Promise<PasswordlessStartResult> {
  const data = await tenxRequest<PasswordlessStartResult>("/v2/auth/passwordless/start", {
    method: "POST",
    body: { email, intent, ...openAnalystPasswordlessMarker() },
  })
  await applyAuthTokens(data)
  return data
}

export type PasswordlessConfirmResult = AuthTokenPayload & {
  ok?: boolean
}

export type PasswordlessConfirmInput =
  | {
      email: string
      code: string
      session: string
      intent?: PasswordlessIntent
    }
  | {
      authIntentId: string
      code: string
    }

// Dev-only AuthIntent preview mock. In a dev build, an `oai_dev_<starterKey>`
// id resolves locally (NO 10x call) to a canned preview whose starterIntent is
// the suffix — so you can open `/login?authIntentId=oai_dev_google-ads-growth-review`
// and watch the login page skip email, reach the OTP screen with starter
// context, and (via the OnboardingV2Redirect exemption) NOT bounce to onboarding.
// The OTP confirm itself still hits the real backend and will fail for a dev id;
// this mock is for visually verifying preview + the deep-link redirect guard.
// No-op in production builds (import.meta.env.DEV is false there).
const DEV_AUTH_INTENT_PREFIX = "oai_dev_"
function devAuthIntentPreview(authIntentId: string): AuthIntentPreview | null {
  if (!import.meta.env.DEV) return null
  if (!authIntentId.startsWith(DEV_AUTH_INTENT_PREFIX)) return null
  const starterKey = authIntentId.slice(DEV_AUTH_INTENT_PREFIX.length).trim() || "google-ads-growth-review"
  const pretty = starterKey.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  return {
    authIntentId,
    destination: "d****v@example.com",
    title: `Start your ${pretty}`,
    subtitle: "Dev AuthIntent preview (local mock — no 10x call). Confirm still needs the real backend.",
    partnerDisplayName: "OpenAnalyst (dev)",
    expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
    starterIntent: {
      key: starterKey,
      title: pretty,
      subtitle: `Local dev starter preview for "${starterKey}".`,
      reason: "Injected by the dev AuthIntent mock.",
      ctaLabel: `Create ${pretty} workspace`,
    },
  }
}

export async function previewAuthIntent(authIntentId: string): Promise<AuthIntentPreview> {
  const devPreview = devAuthIntentPreview(authIntentId)
  if (devPreview) return devPreview
  const data = await tenxRequest<AuthIntentPreview>(`/v2/auth/openanalyst/auth-intents/${encodeURIComponent(authIntentId)}/preview`)
  return {
    authIntentId,
    destination: data.destination ?? null,
    title: data.title ?? null,
    subtitle: data.subtitle ?? null,
    partnerDisplayName: data.partnerDisplayName ?? null,
    expiresAt: data.expiresAt ?? null,
    starterIntent: data.starterIntent ?? null,
  }
}

export async function confirmPasswordlessSignIn(input: PasswordlessConfirmInput): Promise<PasswordlessConfirmResult> {
  const data = "authIntentId" in input
    ? await tenxRequest<PasswordlessConfirmResult>("/v2/auth/openanalyst/auth-intents/confirm", {
        method: "POST",
        body: { authIntentId: input.authIntentId, code: input.code.trim() },
      })
    : await tenxRequest<PasswordlessConfirmResult>("/v2/auth/passwordless/confirm", {
        method: "POST",
        body: { ...input, code: input.code.trim(), intent: input.intent ?? "signin", ...openAnalystPasswordlessMarker() },
      })
  await applyAuthTokens(data)
  return data
}

export async function initAuth(email: string) {
  const path = isDesktop ? "/api/v1/userAccount/initAuth?source=desktop" : "/api/v1/userAccount/initAuth"
  const result = await request<{
    currentStep: number
    allowedMethods: string[]
    authProviders?: { google?: { clientId?: string } }
  }>(path, {
    method: "POST",
    body: { email },
    withSessionToken: false,
  })
  const token = result.response.headers.get("x-session-token")
  if (token) storage.setSessionToken(token)
  return result.data
}

export async function signInWithPassword(email: string, password: string) {
  await initAuth(email)
  await authenticateWithPassword(password, email)
}

export type AuthenticateResult = {
  isEmailVerified: boolean
  email?: string
}

export async function authenticateWithPassword(password: string, email?: string): Promise<AuthenticateResult> {
  const token = storage.getSessionToken()
  if (!token && !email) {
    throw new AuthApiError({
      status: 400,
      message: "Authentication init failed: missing session token and email",
      code: "MISSING_SESSION_TOKEN",
    })
  }
  try {
    const body: Record<string, unknown> = {
      method: "password",
      credentials: { password },
    }
    if (email) body.email = email

    const path = isDesktop ? "/api/v1/userAccount/authenticate?source=desktop" : "/api/v1/userAccount/authenticate"
    const { data } = await request<{
      accessToken?: string
      refreshToken?: string
      isEmailVerified?: boolean
      email?: string
    }>(path, {
      method: "POST",
      body,
    })

    if (data?.isEmailVerified !== false && data?.accessToken) {
      setAccessToken(data.accessToken, "openanalyst")
    }

    return {
      isEmailVerified: data?.isEmailVerified ?? true,
      email: data?.email ?? email,
    }
  } finally {
    storage.clearSessionToken()
  }
}

export async function signInWithGoogle(credential: string): Promise<AuthenticateResult> {
  const { data } = await request<{
    accessToken?: string
    isEmailVerified?: boolean
    email?: string
  }>("/api/v1/userAccount/google-sso", {
    method: "POST",
    body: { credential },
    withSessionToken: false,
  })

  if (data?.accessToken) setAccessToken(data.accessToken, "openanalyst")

  return {
    isEmailVerified: data?.isEmailVerified ?? true,
    email: data?.email,
  }
}

export type SignUpResult = {
  isEmailVerified: boolean
  email: string
}

export async function signUp(input: SignUpInput): Promise<SignUpResult> {
  const fullName = `${input.firstName} ${input.lastName}`.trim()
  const redirectUrl = typeof window !== "undefined" ? window.location.origin : undefined
  const { data } = await request<{
    isEmailVerified?: boolean
    user?: { email?: string }
  }>("/api/v1/org", {
    method: "POST",
    body: {
      accountType: "individual",
      contactEmail: input.email,
      adminFullName: fullName,
      password: input.password,
      redirectUrl,
    },
    withSessionToken: false,
  })

  if (data?.isEmailVerified === false) {
    return {
      isEmailVerified: false,
      email: input.email,
    }
  }

  await signInWithPassword(input.email, input.password)

  return {
    isEmailVerified: true,
    email: input.email,
  }
}

export async function forgotPassword(email: string) {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {}
  const appUrl = env.VITE_APP_URL || "https://app.openanalyst.com"
  const redirectUrl = typeof window !== "undefined" ? (isDesktop ? appUrl : window.location.origin) : undefined
  await request("/api/v1/userAccount/password/forgot", {
    method: "POST",
    body: { email, redirectUrl },
    withSessionToken: false,
  })
}

export async function resetPassword(token: string, password: string) {
  await request("/api/v1/userAccount/password/reset/token", {
    method: "POST",
    body: { password },
    headers: {
      Authorization: `Bearer ${token}`,
    },
    withSessionToken: false,
  })
}

type AuthSessionPayload = {
  userId?: unknown
  email?: unknown
  orgId?: unknown
  workspaceId?: unknown
  projectId?: unknown
  role?: unknown
  bindingTenantId?: unknown
  bindingVersion?: unknown
  accountType?: unknown
  exp?: unknown
}

type OpenAnalystAccountPayload = AuthSessionPayload & {
  status?: unknown
  onboardingStatus?: unknown
  emailVerified?: unknown
  desktopId?: unknown
  defaultDesktopId?: unknown
  clientName?: unknown
  selectedHandle?: unknown
  suggestedHandle?: unknown
  availableHandles?: unknown
  fullName?: unknown
  tenant?: {
    tenantId?: unknown
    ownerUserId?: unknown
  } | null
  defaultWorkspace?: {
    id?: unknown
    workspaceId?: unknown
    projectId?: unknown
    role?: unknown
  } | null
}

function cleanString(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null
}

function cleanRole(value: unknown): AuthRole | null {
  if (value === "owner" || value === "admin" || value === "member") return value
  return null
}

function cleanBindingVersion(value: unknown) {
  if (typeof value === "number" && Number.isInteger(value) && value > 0) return value
  if (typeof value !== "string" || !value.trim()) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function cleanExp(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value
  if (typeof value !== "string" || !value.trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function cleanAccountType(value: unknown): AuthSession["accountType"] {
  if (value === "individual" || value === "business") return value
  return undefined
}

function requiresDesktopOnboarding(payload: Pick<OpenAnalystAccountPayload, "status" | "onboardingStatus">) {
  return payload.status === "required" || payload.onboardingStatus === "onboarding_required"
}

function normalizeAvailableHandle(value: unknown) {
  if (!value || typeof value !== "object") return null
  const handle = cleanString((value as { handle?: unknown }).handle)
  const tenantId = cleanString((value as { tenantId?: unknown }).tenantId)
  const tenantName =
    cleanString((value as { tenantName?: unknown }).tenantName) ||
    cleanString((value as { handleDisplay?: unknown }).handleDisplay) ||
    handle
  const role = cleanString((value as { role?: unknown }).role) || "member"
  if (!handle || !tenantId || !tenantName || !role) return null
  return { handle, tenantId, tenantName, role }
}

function normalizeDesktopOnboarding(payload: OpenAnalystAccountPayload): PendingDesktopOnboarding {
  return {
    status: "required",
    email: cleanString(payload.email),
    emailVerified: payload.emailVerified === true,
    desktopId: cleanString(payload.desktopId),
    defaultDesktopId: cleanString(payload.defaultDesktopId),
    clientName: cleanString(payload.clientName),
    suggestedHandle: cleanString(payload.suggestedHandle) || cleanString(payload.selectedHandle),
    availableHandles: Array.isArray(payload.availableHandles)
      ? payload.availableHandles
        .map(normalizeAvailableHandle)
        .filter((item): item is { handle: string; tenantId: string; tenantName: string; role: string } => !!item)
      : [],
  }
}

function normalizeSession(payload: AuthSessionPayload): AuthSession | null {
  const userId = cleanString(payload.userId)
  const email = cleanString(payload.email)
  const orgId = cleanString(payload.orgId)
  const workspaceId = cleanString(payload.workspaceId)
  const role = cleanRole(payload.role)
  const bindingTenantId = cleanString(payload.bindingTenantId)
  const bindingVersion = cleanBindingVersion(payload.bindingVersion)
  const projectId = cleanString(payload.projectId)
  const exp = cleanExp(payload.exp)

  if (!userId || !email || !orgId || !workspaceId || !role || !bindingTenantId || !bindingVersion || !exp) return null

  return {
    userId,
    email,
    orgId,
    workspaceId,
    role,
    bindingTenantId,
    bindingVersion,
    accountType: cleanAccountType(payload.accountType),
    exp,
    ...(projectId ? { projectId } : {}),
  }
}

function normalizeDesktopAccountPayload(payload: OpenAnalystAccountPayload): AuthSession | null {
  const tenantId = cleanString(payload.tenant?.tenantId)
  const userId = cleanString(payload.userId) || desktopAuthContext.userId
  const orgId = cleanString(payload.orgId) || tenantId || desktopAuthContext.orgId || desktopAuthContext.bindingTenantId
  const workspaceId =
    cleanString(payload.workspaceId) ||
    cleanString(payload.defaultWorkspace?.workspaceId) ||
    cleanString(payload.defaultWorkspace?.id) ||
    desktopAuthContext.workspaceId ||
    orgId
  const role =
    cleanRole(payload.role) ||
    cleanRole(payload.defaultWorkspace?.role) ||
    desktopAuthContext.role ||
    (userId && cleanString(payload.tenant?.ownerUserId) === userId ? "owner" : "member")

  return normalizeSession({
    userId,
    email: cleanString(payload.email) || desktopAuthContext.userEmail,
    orgId,
    workspaceId,
    projectId: cleanString(payload.projectId) || cleanString(payload.defaultWorkspace?.projectId) || desktopAuthContext.projectId,
    role,
    bindingTenantId: cleanString(payload.bindingTenantId) || desktopAuthContext.bindingTenantId || tenantId || orgId,
    bindingVersion: cleanBindingVersion(payload.bindingVersion) || desktopAuthContext.bindingVersion || 1,
    accountType: cleanAccountType(payload.accountType) || desktopAuthContext.accountType,
    exp: cleanExp(payload.exp) || desktopAuthContext.exp || Math.floor(Date.now() / 1000) + 3600,
  })
}

function desktopFallbackSession() {
  return normalizeDesktopAccountPayload({
    userId: desktopAuthContext.userId,
    email: desktopAuthContext.userEmail,
    orgId: desktopAuthContext.orgId || desktopAuthContext.bindingTenantId,
    workspaceId: desktopAuthContext.workspaceId,
    projectId: desktopAuthContext.projectId,
    role: desktopAuthContext.role,
    bindingTenantId: desktopAuthContext.bindingTenantId,
    bindingVersion: desktopAuthContext.bindingVersion,
    accountType: desktopAuthContext.accountType,
    exp: desktopAuthContext.exp,
  })
}

function desktopAccountProfile(payload: OpenAnalystAccountPayload): AuthProfile | null {
  const session = normalizeDesktopAccountPayload(payload)
  if (!session) return null
  return {
    _id: session.userId,
    userId: session.userId,
    email: session.email,
    fullName: cleanString(payload.fullName) ?? desktopAuthContext.fullName ?? undefined,
    firstName: desktopAuthContext.firstName ?? undefined,
    lastName: desktopAuthContext.lastName ?? undefined,
    orgId: session.orgId,
    workspaceId: session.workspaceId,
    projectId: session.projectId,
    role: session.role,
    accountType: session.accountType,
  }
}

export async function checkSession(): Promise<AuthSession | null> {
  try {
    if (isDesktop) {
      const result = await openAnalystAccountRequest<OpenAnalystAccountPayload>("/v2/account/openanalyst/me")
      if (requiresDesktopOnboarding(result.data)) {
        const onboarding = await openAnalystAccountRequest<OpenAnalystAccountPayload>("/v2/account/openanalyst/onboarding")
          .then((next) => next.data)
          .catch(() => result.data)
        throw new OpenAnalystOnboardingRequiredError(normalizeDesktopOnboarding(onboarding))
      }
      const session = normalizeDesktopAccountPayload(result.data)
      if (session) return session
      throw new AuthApiError({
        status: 401,
        message: "OpenAnalyst account facade returned an incomplete desktop auth context.",
        code: "MISSING_OPENANALYST_CONTEXT",
      })
    }
    const result = await request<AuthSessionPayload>("/api/v1/userAccount/me", {
      withSessionToken: false,
      authToken: "account",
    })
    const session = normalizeSession(result.data)
    if (session) return session
    throw new AuthApiError({
      status: 401,
      message: "OpenAnalyst auth context is missing fixed workspace binding claims.",
      code: "MISSING_OPENANALYST_CONTEXT",
    })
  } catch (error) {
    if (error instanceof AuthApiError && error.status === 401) {
      if (isDesktop) return desktopFallbackSession()
      return null
    }
    throw error
  }
}

export async function getUserProfile(userId: string) {
  if (isDesktop) {
    try {
      const result = await openAnalystAccountRequest<OpenAnalystAccountPayload>("/v2/account/openanalyst/me")
      const profile = desktopAccountProfile(result.data)
      if (profile && (profile.userId === userId || profile._id === userId)) return profile
    } catch (error) {
      if (!(error instanceof AuthApiError) || error.status !== 401) throw error
    }
    const session = desktopFallbackSession()
    if (session && session.userId === userId) {
      return {
        _id: session.userId,
        userId: session.userId,
        email: session.email,
        fullName: desktopAuthContext.fullName ?? undefined,
        firstName: desktopAuthContext.firstName ?? undefined,
        lastName: desktopAuthContext.lastName ?? undefined,
        orgId: session.orgId,
        workspaceId: session.workspaceId,
        projectId: session.projectId,
        role: session.role,
        accountType: session.accountType,
      }
    }
  }
  try {
    const result = await request<AuthProfile>(`/api/v1/users/${encodeURIComponent(userId)}`, {
      withSessionToken: false,
    })
    return result.data
  } catch (error) {
    if (!(error instanceof AuthApiError) || (error.status !== 401 && error.status !== 403)) throw error
    const session = await checkSession()
    if (!session || session.userId !== userId) throw error
    return {
      _id: session.userId,
      userId: session.userId,
      email: session.email,
      orgId: session.orgId,
      workspaceId: session.workspaceId,
      projectId: session.projectId,
      role: session.role,
      accountType: session.accountType,
    }
  }
}

export async function refreshAccess() {
  const data = await tenxRequest<AuthTokenPayload>("/v2/auth/refresh", {
    method: "POST",
  })

  if (!(await applyAuthTokens(data))) {
    throw new AuthApiError({ status: 401, message: "Refresh response did not include an access token." })
  }
}

export async function logout() {
  await tenxRequest("/v2/auth/logout", {
    method: "POST",
  }).catch(() => undefined)
  if (isDesktop) {
    await clearStoredTokens()
    return
  }
}

export function resetTokenFromHash(hash: string) {
  if (!hash.includes("token=")) return null
  const token = hash.split("token=")[1]?.split("&")[0]
  if (!token) return null
  return decodeURIComponent(token)
}

export async function verifyEmail(token: string) {
  const { data } = await request<{
    success: boolean
    message: string
    accessToken?: string
    alreadyVerified?: boolean
    expiresIn?: number
  }>(`/api/v1/userAccount/verify-email?token=${encodeURIComponent(token)}`, {
    method: "GET",
    withSessionToken: false,
  })

  if (data?.accessToken) setAccessToken(data.accessToken, "openanalyst")

  return data
}

export async function resendVerification(email: string) {
  const redirectUrl = typeof window !== "undefined" ? window.location.origin : undefined
  const { data } = await request<{
    success: boolean
    message: string
  }>("/api/v1/userAccount/resend-verification", {
    method: "POST",
    body: { email, redirectUrl },
    withSessionToken: false,
  })

  return data
}

export const ACCOUNT_LOCKED_CODE = "ACCOUNT_LOCKED"
export const ACCOUNT_BLOCKED_CODE = "ACCOUNT_BLOCKED"
export const INVALID_CREDENTIALS_CODE = "INVALID_CREDENTIALS"

export interface LockoutMetadata {
  code: string
  retryAfter?: number
  lockedUntil?: string | null
  durationMinutes?: number | null
  permanent?: boolean
  attemptsRemaining?: number
}

export function extractLockoutMetadata(err: unknown): LockoutMetadata | null {
  if (!(err instanceof AuthApiError)) return null
  const meta = err.metadata
  if (!meta || typeof meta !== "object") return null
  const record = meta as Record<string, unknown>
  const code = typeof record.code === "string" ? record.code : undefined
  if (!code) return null
  if (code !== ACCOUNT_LOCKED_CODE && code !== ACCOUNT_BLOCKED_CODE && code !== INVALID_CREDENTIALS_CODE) {
    return null
  }
  return {
    code,
    retryAfter: typeof record.retryAfter === "number" ? record.retryAfter : undefined,
    lockedUntil:
      typeof record.lockedUntil === "string" || record.lockedUntil === null
        ? (record.lockedUntil as string | null)
        : undefined,
    durationMinutes: typeof record.durationMinutes === "number" ? record.durationMinutes : undefined,
    permanent: typeof record.permanent === "boolean" ? record.permanent : undefined,
    attemptsRemaining: typeof record.attemptsRemaining === "number" ? record.attemptsRemaining : undefined,
  }
}

export function isTemporaryLockout(meta: LockoutMetadata | null): boolean {
  if (!meta) return false
  return meta.code === ACCOUNT_LOCKED_CODE && typeof meta.retryAfter === "number" && meta.retryAfter > 0
}
